import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { useAdminActionCollections } from '../../../context/AdminActionCollections';

const COIReportFilters = ({
  isAdmin,
  isSCSecretary,
  isSCObserver,
  isCouncilMember,
  availableCommittees,
  selectedCommittee,
  sectionalCommitteesWithCmse,
  onCommitteeChange,
  getCommitteeCandidateCount,
  showCouncilMembersOnly,
  onMemberTypeChange,
}) => {
  const { enableCouncilMembers } = useAdminActionCollections();

  const hasSetInitialMemberType = useRef(false);

  useEffect(() => {
    if (!hasSetInitialMemberType.current && isCouncilMember && !isAdmin) {
      onMemberTypeChange(true);
      hasSetInitialMemberType.current = true;
    }
  }, [isCouncilMember, isAdmin, onMemberTypeChange]);

  const getCommitteeTitle = () => {
    if (!selectedCommittee) {
      return 'Filter by Sectional Committee';
    }
    const committee = sectionalCommitteesWithCmse.find(
      (item) => item.id === parseInt(selectedCommittee),
    );
    return committee?.attributes.name || 'Filter by Sectional Committee';
  };

  const getMemberTypeTitle = () =>
    showCouncilMembersOnly ? 'Council Members' : 'Chairs and SC Members';

  const shouldShowCommitteeFilter =
    isAdmin ||
    isSCSecretary ||
    isCouncilMember ||
    (isSCObserver && availableCommittees.length > 1);

  if (
    !isAdmin &&
    !isSCSecretary &&
    !isCouncilMember &&
    !isSCObserver &&
    availableCommittees.length === 0
  ) {
    return null;
  }

  return (
    <div className="table-controls mt-xl-5 mb-4 d-flex align-items-center gap-2">
      {shouldShowCommitteeFilter && (
        <DropdownButton
          id="committee-selector"
          variant="outline-primary"
          title={getCommitteeTitle()}
        >
          <Dropdown.Item onClick={() => onCommitteeChange(null)}>
            All
          </Dropdown.Item>
          {availableCommittees.map((item) => {
            const count = getCommitteeCandidateCount
              ? getCommitteeCandidateCount(item.id)
              : null;
            const hasNoCandidates = count === 0;

            return (
              <Dropdown.Item
                key={item.id}
                onClick={() => !hasNoCandidates && onCommitteeChange(item.id)}
                disabled={hasNoCandidates}
                className={hasNoCandidates ? 'text-muted' : ''}
              >
                {item.attributes.name}
                {count !== null && (
                  <span
                    className={`ms-2 ${hasNoCandidates ? 'text-muted' : 'text-primary'}`}
                  >
                    ({count})
                  </span>
                )}
              </Dropdown.Item>
            );
          })}
        </DropdownButton>
      )}

      {enableCouncilMembers && (
        <DropdownButton
          id="member-type-selector"
          variant="outline-primary"
          title={getMemberTypeTitle()}
        >
          <Dropdown.Item
            onClick={() => onMemberTypeChange(false)}
            active={!showCouncilMembersOnly}
          >
            Chairs and SC Members
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => onMemberTypeChange(true)}
            active={showCouncilMembersOnly}
          >
            Council Members
          </Dropdown.Item>
        </DropdownButton>
      )}
    </div>
  );
};

COIReportFilters.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  isSCSecretary: PropTypes.bool.isRequired,
  isSCObserver: PropTypes.bool,
  isCouncilMember: PropTypes.bool,
  availableCommittees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      attributes: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
    }),
  ).isRequired,
  selectedCommittee: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sectionalCommitteesWithCmse: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      attributes: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
    }),
  ).isRequired,
  onCommitteeChange: PropTypes.func.isRequired,
  getCommitteeCandidateCount: PropTypes.func,
  showCouncilMembersOnly: PropTypes.bool.isRequired,
  onMemberTypeChange: PropTypes.func.isRequired,
};

COIReportFilters.defaultProps = {
  selectedCommittee: null,
  getCommitteeCandidateCount: null,
  isSCObserver: false,
  isCouncilMember: false,
};

export default COIReportFilters;
